import React from "react";

import {
  TextInput,
  Select,
  Text,
  Avatar,
  Textarea,
  NumberInput,
  Switch,
} from "@mantine/core";

import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";

import RemoteSelect2 from "../../components/remoteSelect2";
import ActionCellRender from "../../components/actionCellRender";
import { IconTrash, IconPencil, IconSettings } from "@tabler/icons";
import { DATA_ROUND_ON } from "../../data/options";
import OverridingTable from "../../components/overridingTable";
import OverridingTables from "../../components/overridingTables";
import OverridingTester from "../../components/overridingTester";
import NoMoreParentToField from "../../components/noMoreParentToField";

const apiEntity = "commissionOverridingPlan";

const fieldGuide = (title, body) => ({ title, body });

const PLAN_FIELD_GUIDES = {
  name: fieldGuide(
    "Plan Name",
    "The name of this overriding plan. It is the name shown when a consultant is assigned a plan."
  ),
  moneyRoundDigit: fieldGuide(
    "Money Round Digit",
    "How many decimal places a money amount keeps. Amounts are rounded down. 2 keeps cents, so 10.239 becomes 10.23. 0 rounds down to a whole number."
  ),
  rateRoundDigit: fieldGuide(
    "Rate Round Digit (%)",
    "How many decimal places the commission percentage keeps when Round On Rate is on. Example: 65.25% × 75% = 48.9375%. Digit 2 rounds that down to 48.93%. This digit is not used when Round On Rate is off, or when Allow Direct on Premium is on."
  ),
  desc: fieldGuide(
    "Description",
    "A note about this plan. It is stored with the plan and shown in the list. It does not change the commission calculation."
  ),
  isDefault: fieldGuide(
    "Default plan",
    "Consultants who have no overriding plan receive this plan, including when an order is created. Only one plan can be the default. Turning this on removes the default from every other plan. A consultant who already has a plan keeps that plan."
  ),
  roundOnRate: fieldGuide(
    "Round On Rate",
    "On: the provider rate is multiplied by the level percentage, that percentage is rounded down with Rate Round Digit, then the premium and the selected exchange rate are applied. The money is rounded down with Money Round Digit. Off: each level is premium × provider rate × the selected exchange rate × that level’s percentage, then rounded down with Money Round Digit. The provider rate and the level percentage are not combined first. Rate Round Digit is not used. Allow Direct on Premium ignores this switch."
  ),
  useInternalExchangeRate: fieldGuide(
    "Use Internal Currency Exchange Rate",
    "On: each level is calculated with the internal exchange rate when it is lower than the provider rate. Off: each level uses the provider exchange rate. If the internal rate is higher, the provider rate is used either way. The remain level receives the provider total minus the sum of every level's rounded share. That remainder includes the exchange-rate gap and any amount left by rounding. Round On Rate can leave a larger remainder than the exchange-rate gap alone."
  ),
  allowSkipProviderRate: fieldGuide(
    "Allow Direct on Premium (Skip Product Rate)",
    "On: the provider commission rate is skipped. Each level is paid premium × that level’s percentage × the exchange rate, rounded down with Money Round Digit. Rate Round Digit is not used, and Round On Rate is ignored. Off: the provider rate is part of the commission, and Round On Rate decides whether the percentage or the amount is rounded first."
  ),
  notProductCategory: fieldGuide(
    "Not allow to sell product category",
    "Product categories in this list cannot be sold by a consultant who uses this plan. Creating an order in one of these categories is stopped."
  ),
  notProductTypes: fieldGuide(
    "Not allow to sell product types",
    "Product types in this list cannot be sold by a consultant who uses this plan. Creating an order of one of these types is stopped."
  ),
};

const tableSchema = {
  showActionColumn: true,
  columns: [
    { field: "name", headerName: "Name" },
    { field: "desc", headerName: "Description" },
    {
      field: "isDefault",
      headerName: "Default",
      cellRender: (col, data) =>
        data.isDefault ? (
          <Text size="xs" color="blue">
            Yes
          </Text>
        ) : (
          ""
        ),
    },

    {
      field: "action",
      cellRender: ActionCellRender,
      isAction: true,
      actions: [
        {
          name: "edit",
          icon: IconPencil,
          props: {
            //   color:"red"
          },
        },
      ],
      actionMenu: [
        {
          name: "delete",
          label: "Delete data",
          icon: IconTrash,
          props: {
            color: "red",
          },
        },
      ],
    },
  ],

  searchableFields: ["code", "name"],
};

const variationFromYearError = (tables) => {
  if (!Array.isArray(tables)) return null;
  for (const table of tables) {
    const year = Number(table?.fromYear);
    if (!Number.isFinite(year) || year < 1) {
      return `${table?.name || "Variation"}: From Year is required`;
    }
  }
  return null;
};

const formSchema = {
  title: "Commission Overriding Plan",
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
  },
  apiEntity,
  validate: {
    specialTables: variationFromYearError,
  },
  initialValues: {
    name: "",
    roundOnRate: true,
    useInternalCurrency: true,
    rateRoundDigit: 2,
    rateRoundUnit: "percent",
    moneyRoundDigit: 2,

    status: "ACTIVE",
    isDefault: false,
    noMoreParentTo: null,
    table: [
      {
        userGroup: null,
        isRemainLevel: false,
        row: 1,
        level: 0,
        index: 1,
        percentage: 0,
      },
    ],
  },

  layout: {
    containers: [
      {
        key: "main-tab",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default", //default | pills | outline
          defaultValue: "Plan Details",
        },
        tab: [
          {
            key: "tab-detail",
            label: "Plan Details",
            icon: IconSettings,
          },
          { key: "tab-preview", label: "Tester", icon: IconSettings },
          { key: "tab-product", label: "Product Control", icon: IconSettings },
        ],
      },
      {
        key: "info-section",
        parent: "tab-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Basic Info",
          description: "Please fill in the Payment info of product",
        },
      },

      {
        key: "level-section",
        parent: "tab-detail",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Overriding Levels",
          description: "Please fill in the overriding levels info",
        },
      },

      {
        key: "test-section",
        parent: "tab-preview",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Plan Tester",
          description: "You can test the overriding plan",
        },
      },
      {
        key: "product-section",
        parent: "tab-product",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Product Settings",
          description: "You can set the product settings here",
        },
      },
      {
        key: "info-g",
        parent: "info-section",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        props: {
          gutter: "xl",
        },
        cols: [
          {
            key: "info-g1",
            props: {
              span: 6,
            },
          },
          {
            key: "info-g2",
            props: {
              span: 6,
            },
          },
        ],
      },
    ],
    fields: [
      {
        name: "name",
        parent: "info-g1",
        component: TextInput,
        props: {
          required: true,
          label: "Plan Name",
          placeholder: "Basic Plan 1",
        },
        guide: PLAN_FIELD_GUIDES.name,
      },
      {
        name: "moneyRoundDigit",
        parent: "info-g1",
        component: NumberInput,
        props: {
          label: "Money Round Digit",
          required: true,
          min: 0,
          max: 5,
        },
        guide: PLAN_FIELD_GUIDES.moneyRoundDigit,
      },
      {
        name: "rateRoundDigit",
        parent: "info-g1",
        component: NumberInput,
        props: {
          label: "Rate Round Digit (%)",
          required: true,
          min: 0,
          max: 5,
        },
        guide: PLAN_FIELD_GUIDES.rateRoundDigit,
      },
      {
        name: "noMoreParentTo",
        parent: "info-g1",
        component: NoMoreParentToField,
        props: {
          label: "No more parent pays",
        },
      },
      {
        name: "desc",
        parent: "info-g1",
        component: Textarea,
        props: {
          label: "Description",
        },
        guide: PLAN_FIELD_GUIDES.desc,
      },
      {
        name: "isDefault",
        parent: "info-g2",
        component: Switch,
        props: {
          type: "checkbox",
          label: "Default plan",
        },
        guide: PLAN_FIELD_GUIDES.isDefault,
      },
      {
        name: "roundOnRate",
        parent: "info-g2",
        component: Switch,
        props: {
          type: "checkbox",
          label: "Round On Rate",
        },
        guide: PLAN_FIELD_GUIDES.roundOnRate,
      },
      {
        name: "useInternalExchangeRate",
        parent: "info-g2",
        component: Switch,
        props: {
          type: "checkbox",
          label: "Use Internal Currency Exchange Rate",
        },
        guide: PLAN_FIELD_GUIDES.useInternalExchangeRate,
      },
      {
        name: "allowSkipProviderRate",
        parent: "info-g2",
        component: Switch,
        props: {
          type: "checkbox",
          label: "Allow Direct on Premium (Skip Product Rate)",
        },
        guide: PLAN_FIELD_GUIDES.allowSkipProviderRate,
      },
      {
        name: "table",
        parent: "level-section",
        component: OverridingTables,
        props: {
          lable: "Overrding Tables",
        },
      },

      // {
      //   name: "table",
      //   parent: "level-section",
      //   component: OverridingTable,
      //   props: {
      //     lable: "Overrding Table",
      //   },
      // },

      {
        name: "notProductCategory",
        parent: "product-section",

        component: RemoteSelect2,
        props: {
          label: "Not allow to sell product category",
          apiEntity: "productCategory",

          placeholder: "Search",
          valueField: "_id",
          labelField: "name",
          allowClear: true,
          labelRender: (data) => `${data.name} ${data.cname}`,
          multiple: true,
          pageSize: 200,
          // preQuery: { $and: [ {isConsultant : true}, {isActive: true} ] },
          // preQuery:  {isConsultant : true} ,
          searchFields: ["name", "code", "cname"],
          clearable: true,
          sort: {
            by: "name",
            order: "asc",
          },
        },
        guide: PLAN_FIELD_GUIDES.notProductCategory,
      },
      {
        name: "notProductTypes",
        parent: "product-section",
        component: RemoteSelect2,
        props: {
          label: "Not allow to sell product types",
          apiEntity: "productType",

          placeholder: "Search",
          valueField: "_id",
          labelField: "name",
          allowClear: true,
          labelRender: (data) => `${data.name} ${data.cname}`,
          multiple: true,
          pageSize: 200,
          // preQuery: { $and: [ {isConsultant : true}, {isActive: true} ] },
          // preQuery:  {isConsultant : true} ,
          searchFields: ["name", "code", "cname"],
          clearable: true,
          sort: {
            by: "name",
            order: "asc",
          },
        },
        guide: PLAN_FIELD_GUIDES.notProductTypes,
      },

      {
        name: "table",
        parent: "test-section",
        component: OverridingTester,
        props: {},
      },
    ],
  },
};

const CommissionOverridingPlan = () => {
  return (
    <PageList
      title={"Commission Overriding Plan"}
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
    />
  );
};

export default CommissionOverridingPlan;
